define([
	"underscore",
	"csi/html",
	"csi/monitor/_base"
], function(_,html,monitor) {

	/**
	 * @typedef {Object} AttributeMonitorContext
	 * Object holding properties necessary to monitor an attribute value and report progress and changes
	 * @constant {Context} ctx - context for rendered heading
	 * @constant {Heading} heading - the rendered heading
	 * @constant {string} [idStatus] - id used to hold status for heading
	 * @constant {Object} nodeMonitorHandle - handle to node monitor
	 * @constant {DOMNode} refNode - DOMNode for rendered heading
	 * @constant {Renderer} ren - heading renderer
	 * @property {Object} [params] - other parameters that may be needed to monitor attribute
	 * @constant {string} urlMonitor - node url containing monitored attribute
	 * @property {any} value - value being monitored
	 */

	const _monitors = [];
	let _timer;
	
	/**
	 * Respond to a change in a node that is being monitored
	 * @param {AttributeMonitorContext} amc 
	 * @param {Node} node 
	 */
	function _onChangeNode(amc, node) {
		const {
			ctx,
			heading,
			idStatus,
			refNode,
			ren,
			value,
		} = amc;

		if (html.inDOM(refNode)) {
			let newValue;
	
			ctx.cache.put(node);
	
			if (idStatus) {
				newValue = node[idStatus];
			} else {
				newValue = ren._getValue(ctx, heading);
			}
	
			if (value == newValue) {
				// no change in monitored value, but there was some change, so report any progress
				ren.onMonitorProgress(amc, value);
			} else {
				// change in monitored value, so notify renderer of change; remove if onMonitor returns true
				if (ren.onMonitor(amc, newValue)) {
					remove(amc);
				} else {
					// if not done monitoring, then update value
					amc.value = newValue;
				}
			}
		} else {
			// stop monitoring attribute if no longer in DOM
			remove(amc);
		}
	}

	/**
	 * Loop through all attribute monitors and remove any that are no longer in the DOM
	 */
	function _onTimer() {
		// reset timeout handle
		_timer = null;

		if (!_monitors.length) {
			return;
		}	

		const monitors = [..._monitors];

		for (const amc of monitors) {
			if (!html.inDOM(amc.refNode)) {
				remove(amc);
			}	
		}	

		// restart timer if there are still attributes to monitor
		if (_monitors.length) {
			_startTimer();
		}
	}

	/**
	 * Start timer to check for attribute monitors that are no longer necessary (no longer in DOM)
	 */
	function _startTimer() {
		_timer = _timer || setTimeout(_onTimer, 5000);
	}
	
	/**
	 * Monitor heading for any changes and redraw when changed
	 * @param {AttributeMonitorContext} amc - object specifying the heading and its context necessary to re-render heading
	 */
	function add(amc) {
		_monitors.push(amc);
		amc.nodeMonitorHandle = monitor.onChangeNode(amc.urlMonitor, node => _onChangeNode(amc, node));
		_startTimer();
	}
	
	/**
	 * Stop attribute monitor
	 * @param {AttributeMonitorContext} amc 
	 */
	function remove(amc) {
		amc.nodeMonitorHandle.remove();
		_monitors.dele(amc);
	}
	
	return {
		add,
		remove
	};
});